import { useState } from 'react';
import { GitBranch, ListTodo, Plus } from 'lucide-react';
import type { TaskResponse, TaskWithRelations } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { ClassificationLabelMap } from '@/hooks/use-classification-label';
import { ClassificationBadge } from '@/components/custom/classification-badge';
import { PatientPanel } from './PatientPanel';
import { SummarySection } from './SummarySection';
import { ActivityTimeline } from './ActivityTimeline';
import { TaskListSection } from './TaskListSection';
import { ReferralSourceLine } from './ReferralSourceLine';
import { NextStepSection } from './NextStepSection';
import { LastCallSection } from './LastCallSection';
import { isCallFollowUp } from './call-follow-up-display';
import { useTaskRelated } from './use-task-overview';
import { PatientAppointmentList } from '@/components/appointments/PatientAppointmentList';
import { TestBookingDialog } from '@/components/appointments/TestBookingDialog';
import { useCanAddTestBooking } from '@/hooks/use-test-booking';
import { InteractionDetailSheet } from '@/components/interactions/InteractionDetailSheet';
import { useCallDetailParam } from '@/hooks/use-call-detail-param';

interface TaskOverviewTabProps {
  task: TaskWithRelations;
  labelMap: ClassificationLabelMap;
}

/**
 * Default landing tab on a task. Lays the sections out top-to-bottom:
 * headline + referral source → AI summary → next step (action drawer)
 * → patient (collapsed) → activity timeline (collapsed) → sub-tasks
 * → other open patient tasks.
 *
 * `useTaskRelated` is called once here and split into the two task
 * lists. Earlier each list owned its own subscription — TanStack Query
 * dedupes the network call but the two `useQuery` subscriptions still
 * re-rendered together on every state transition.
 */
export function TaskOverviewTab({ task, labelMap }: TaskOverviewTabProps): React.ReactElement {
  const { data: related, isLoading: relatedLoading } = useTaskRelated(task.id);
  // ONE sheet for the whole tab, keyed off `?call=` in the URL. Both
  // `LastCallSection` and `ActivityTimeline` used to own a sheet each, which
  // was fine while each held its own boolean — but a shared URL param would
  // have opened both on the same id and stacked them. They report the click;
  // this owns the sheet.
  const { openCallId, openCall, closeCall } = useCallDetailParam();
  // Platform admins in a demo practice only (ADR-293); the API decides.
  const canAddTestBooking = useCanAddTestBooking();
  const [testBookingOpen, setTestBookingOpen] = useState(false);

  return (
    <div className="absolute inset-0 overflow-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <header className="rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-xs capitalize">
              {task.priority}
            </Badge>
            <Badge variant="outline" className="text-xs capitalize">
              {task.status.replace(/_/g, ' ')}
            </Badge>
            {(task.labels ?? []).map((slug) => (
              <ClassificationBadge key={slug} classification={slug} labelMap={labelMap} size="sm" />
            ))}
          </div>
          <h2 className="mt-2 text-base font-semibold leading-snug">{task.title}</h2>
          {/* Skipping `task.description` deliberately for email tasks — the
              email pipeline templates it as "<doc-classification> received.
              Auth#: …", which (a) confused users when classification was
              "other" and (b) duplicates auth #/status, both shown by the
              ReferralSourceLine below. A CALL follow-up's description is the
              work itself (who to ring, what they said), so it is shown. */}
          {isCallFollowUp(task) && task.description && (
            <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
              {task.description}
            </p>
          )}
          <ReferralSourceLine task={task} />
        </header>

        <SummarySection taskId={task.id} />
        {/* How the last call went, ABOVE the next step: the operator reads the
            previous attempt before deciding whether to dial again, so it has to
            come before the Call-to-schedule button rather than after it.
            Renders nothing when the task has no calls. */}
        <LastCallSection taskId={task.id} onOpenCall={openCall} />
        <NextStepSection task={task} />
        <PatientPanel patientId={task.patientId} />
        {/* Patient's upcoming appointments — surfaced so the operator can
            decide whether to start another scheduling call. Would the new
            appointment conflict? Is the patient already booked for what
            this task is about? PatientAppointmentList handles its own
            loading + empty-state rendering, so the section is safe to
            render whenever the task is linked to a patient.
            `upcoming` mode fetches future-dated bookings only (filters out
            past completed/cancelled visits which are noise here). */}
        {task.patientId && (
          <div className="rounded-lg border bg-card">
            <div className="border-b px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Upcoming appointments
              </span>
            </div>
            <div className="p-4">
              <PatientAppointmentList patientId={task.patientId} upcoming limit={10} />
              {canAddTestBooking && (
                <>
                  <Button
                    variant="outline"
                    className="mt-3 w-full border-dashed"
                    onClick={() => setTestBookingOpen(true)}
                  >
                    <Plus className="size-4" />
                    Add an appointment (for TESTING ONLY)
                  </Button>
                  <TestBookingDialog
                    patientId={task.patientId}
                    open={testBookingOpen}
                    onOpenChange={setTestBookingOpen}
                  />
                </>
              )}
            </div>
          </div>
        )}
        <ActivityTimeline taskId={task.id} onOpenCall={openCall} />
        <TaskListSection
          title="Sub-tasks"
          icon={GitBranch}
          tasks={related?.childTasks}
          isLoading={relatedLoading}
          renderBadges={renderChildBadges}
        />
        <TaskListSection
          title="Other open tasks for this patient"
          icon={ListTodo}
          tasks={related?.otherOpenPatientTasks}
          isLoading={relatedLoading}
          renderBadges={renderPatientTaskBadges}
        />
      </div>
      <InteractionDetailSheet
        interactionId={openCallId}
        onOpenChange={(open) => {
          if (!open) closeCall();
        }}
      />
    </div>
  );
}

function renderChildBadges(task: TaskResponse): React.ReactNode {
  return (
    <Badge variant="outline" className="text-xs capitalize">
      {task.status.replace(/_/g, ' ')}
    </Badge>
  );
}

function renderPatientTaskBadges(task: TaskResponse): React.ReactNode {
  return (
    <>
      <Badge variant="outline" className="text-xs capitalize">
        {task.priority}
      </Badge>
      <Badge variant="outline" className="text-xs capitalize">
        {task.status.replace(/_/g, ' ')}
      </Badge>
    </>
  );
}
